import React from 'react';
import { Drawer } from '../base/Drawer';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../../styles/tokens';
import { formatInstant } from '../../utils/datetime';
import { PricingJobDetail } from '../../api/pricing';

interface JobDetailDrawerProps {
  job: PricingJobDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onAction?: (jobId: string, action: 'cancel' | 'pause' | 'resume') => void;
}

const terminalLabels: Record<string, string> = {
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  expired: 'Expired',
  committed: 'Committed',
  discarded: 'Discarded',
};

export function JobDetailDrawer({ job, isOpen, onClose, onAction }: JobDetailDrawerProps) {
  if (!job) return null;

  const infoRow = (label: string, value: React.ReactNode) => (
    <div style={styles.row}>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>{value}</div>
    </div>
  );

  const renderActionButtons = () => {
    const actions: Array<{ key: 'cancel' | 'pause' | 'resume'; label: string }> = [];
    if (job.status === 'running' || job.status === 'queued') {
      actions.push({ key: 'cancel', label: 'Cancel Job' });
      actions.push({ key: 'pause', label: 'Pause' });
    }
    if (job.status === 'paused') {
      actions.push({ key: 'resume', label: 'Resume' });
    }

    if (!actions.length) return null;

    return (
      <div style={styles.actions}>
        {actions.map((action) => (
          <button
            key={action.key}
            style={{ ...styles.actionButton, ...(action.key === 'cancel' ? styles.danger : {}) }}
            onClick={() => onAction?.(job.id, action.key)}
          >
            {action.label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={job.job_name || 'Job Details'}>
      {infoRow('Status', <span style={styles.status}>{terminalLabels[job.status] || job.status}</span>)}
      {infoRow('Channel', job.channel || '—')}
      {infoRow('Variants', job.variant_count)}
      {infoRow('Success', job.success_count)}
      {infoRow('Errors', job.error_count)}
      {infoRow('Skipped', job.skipped_count)}
      {infoRow('Created', formatInstant(job.created_at))}
      {job.started_at && infoRow('Started', formatInstant(job.started_at))}
      {job.completed_at && infoRow('Completed', formatInstant(job.completed_at))}
      {job.error_details && infoRow('Error Details', job.error_details)}

      {job.config && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Config</div>
          <pre style={styles.code}>{JSON.stringify(job.config, null, 2)}</pre>
        </div>
      )}

      {(job.html_report_path || job.csv_export_path) && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Artifacts</div>
          <div style={styles.links}>
            {job.html_report_path && (
              <a href={job.html_report_path} style={styles.link} target="_blank" rel="noreferrer">
                View HTML Report
              </a>
            )}
            {job.csv_export_path && (
              <a href={job.csv_export_path} style={styles.link} target="_blank" rel="noreferrer">
                Download CSV Export
              </a>
            )}
          </div>
        </div>
      )}

      {renderActionButtons()}
    </Drawer>
  );
}

const styles = {
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${spacing.sm} 0`,
    borderBottom: `1px solid ${colors.border}`,
  },
  label: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  value: {
    fontSize: typography.body.fontSize,
    color: colors.text,
    textAlign: 'right' as const,
  },
  status: {
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  section: {
    marginTop: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight as any,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  code: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: spacing.md,
    fontSize: typography.small.fontSize,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap' as const,
  },
  links: {
    display: 'flex',
    gap: spacing.md,
    flexWrap: 'wrap' as const,
  },
  link: {
    color: colors.primary,
    fontWeight: typographyFlat.fontWeight.semibold,
    textDecoration: 'none',
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  actionButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  danger: {
    background: colors.error,
    borderColor: colors.error,
    color: colors.surface,
  },
};
